<!-- Term tab — single Terms & Conditions section. Inline layout (no
     inner-card boxes) so the section card's white surface is the only
     chrome the user sees. Header carries the "+ Create new" toggle;
     body shows the enr-select template picker + HTML preview, or the
     inline create form when authoring a new template. -->
@if (detail(); as d) {
<div class="term">
  <div
    id="section-tc"
    class="enr-section-card enr-section-card--readonly term__form-section"
  >
    <enr-collapsible
      title="Terms & Conditions"
      [expanded]="true"
      ariaLabel="Terms & Conditions"
    >
      <span slot="title" class="flex items-center gap-2">
        <span class="term__title-num" aria-hidden="true">
          {{ sectionNumber }}
        </span>
        <span class="enr-section-card__title">Terms &amp; Conditions</span>
      </span>

      <div class="term__card-body">
        @if (priceAgreementOverrides(); as o) { @if (o.own > 0) {
        <!-- Prices with their own Agreement don't follow this one
             (CONTEXT.md "Price Agreement"). -->
        <div
          class="term__overrides"
          role="status"
          data-testid="term-price-agreement-banner"
        >
          <mat-icon aria-hidden="true" class="term__overrides-icon"
            >info</mat-icon
          >
          <div class="term__overrides-text">
            <strong>
              {{ o.own }} of {{ o.total }} prices use their own Agreement
            </strong>
            <span>
              Changing the Agreement here reaches only the prices that follow
              the offering.
            </span>
          </div>
          <button
            type="button"
            class="term__btn term__btn--ghost"
            data-testid="term-price-agreement-review"
            (click)="reviewPriceAgreements()"
          >
            Review in Pricing
            <mat-icon aria-hidden="true">arrow_forward</mat-icon>
          </button>
        </div>
        } } @if (!creating()) {
        <!-- ── Picker row ─────────────────────────────────────────── -->
        <div class="term__pick">
          <div class="term__pick-field">
            <span class="term__pick-label">Agreement</span>
            <enr-select
              #termPicker
              class="term__select"
              [formControl]="templateControl"
              [options]="templateOptions()"
              placeholder="Choose an Agreement"
              [searchable]="true"
              searchPlaceholder="Search Agreements…"
              (stateChange)="search.onState($event, termPicker)"
              (searchChange)="search.onSearch($event)"
            ></enr-select>
          </div>
          <button
            type="button"
            class="term__btn term__btn--ghost"
            (click)="openCreate()"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            Create new
          </button>
        </div>

        @if (selectedTemplate(); as tpl) { @if (!editing()) {
        <!-- ── Preview (read-only) ────────────────────────────────── -->
        <div class="term__preview-block">
          <header class="term__preview-head">
            <div class="term__preview-meta">
              <mat-icon aria-hidden="true">visibility</mat-icon>
              Preview ·
              <strong>{{ previewName() }}</strong>
              <code class="term__tpl-id">{{ tpl.id }}</code>
            </div>
            <div class="term__preview-head-right">
              <div class="term__locale-tabs" role="tablist">
                @for (l of languages(); track trackLangLocale($index, l)) {
                <button
                  type="button"
                  role="tab"
                  class="term__locale-tab"
                  [class.term__locale-tab--active]="previewLocale() === l.locale"
                  [attr.aria-selected]="previewLocale() === l.locale"
                  (click)="setPreviewLocale(l.locale)"
                >
                  {{ l.shortCode }}
                </button>
                }
              </div>
              <button
                type="button"
                class="term__btn term__btn--ghost"
                [disabled]="loadingPreview()"
                (click)="startEdit()"
              >
                <mat-icon aria-hidden="true">edit</mat-icon>
                Edit
              </button>
            </div>
          </header>

          @if (tpl.agreementType || tpl.status || tpl.initialDate ||
          tpl.specName) {
          <dl class="term__meta">
            @if (tpl.agreementType) {
            <div class="term__meta-row">
              <dt>Type</dt>
              <dd>{{ tpl.agreementType }}</dd>
            </div>
            } @if (tpl.status) {
            <div class="term__meta-row">
              <dt>Status</dt>
              <dd>{{ tpl.status }}</dd>
            </div>
            } @if (tpl.specName) {
            <div class="term__meta-row">
              <dt>Specification</dt>
              <dd>{{ tpl.specName }}</dd>
            </div>
            } @if (tpl.initialDate) {
            <div class="term__meta-row">
              <dt>Initial date</dt>
              <dd>{{ tpl.initialDate | date: "mediumDate" }}</dd>
            </div>
            }
          </dl>
          } @if (loadingPreview()) {
          <div
            class="term__sk-lines"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <span class="term__sr-only">Loading template content…</span>
            <span class="term__sk term__sk--line" aria-hidden="true"></span>
            <span
              class="term__sk term__sk--line term__sk--w-90"
              aria-hidden="true"
            ></span>
            <span
              class="term__sk term__sk--line term__sk--w-75"
              aria-hidden="true"
            ></span>
            <span
              class="term__sk term__sk--line term__sk--w-85"
              aria-hidden="true"
            ></span>
          </div>
          } @else if (previewIsEmpty()) {
          <p class="term__preview-empty">Not authored in this language yet.</p>
          } @else {
          <div class="term__preview" [innerHTML]="previewHtml()"></div>
          }
        </div>
        } @else {
        <!-- ── Edit (modify existing agreement) ───────────────────── -->
        <div class="term__preview-block">
          <header class="term__preview-head">
            <div class="term__preview-meta">
              <mat-icon aria-hidden="true">edit</mat-icon>
              Edit ·
              <strong>{{ tpl.name }}</strong>
              <code class="term__tpl-id">{{ tpl.id }}</code>
            </div>
          </header>

          <div class="term__field-row">
            <label class="term__field">
              <span class="term__field-label">Type</span>
              <input
                type="text"
                class="term__name-input"
                [value]="editType()"
                placeholder="package"
                (input)="onEditTypeInput($event)"
              />
            </label>
            <label class="term__field">
              <span class="term__field-label">Status</span>
              <input
                type="text"
                class="term__name-input"
                [value]="editStatus()"
                placeholder="initialized"
                (input)="onEditStatusInput($event)"
              />
            </label>
          </div>

          <div class="term__lang-stack">
            @for (l of visibleLanguages(); track trackLangLocale($index, l)) {
            <section class="term__lang-card">
              <header class="term__lang-card-head">
                <span
                  class="term__locale-dot"
                  [class.term__locale-dot--on]="
                    (editNameFor(l.locale) || '').trim().length > 0 ||
                    (editContentFor(l.locale) || '').trim().length > 0
                  "
                  aria-hidden="true"
                ></span>
                <span class="term__lang-card-code">{{ l.shortCode }}</span>
                <span class="term__lang-card-label">{{ l.label }}</span>
              </header>

              <div class="term__lang-card-body">
                <label class="term__field">
                  <span class="term__field-label"
                    >Name
                    <span class="term__req" aria-hidden="true">*</span></span
                  >
                  <input
                    type="text"
                    class="term__name-input"
                    [value]="editNameFor(l.locale)"
                    [placeholder]="'Name in ' + l.label"
                    (input)="onEditNameInput(l.locale, $event)"
                  />
                </label>

                <div class="term__field">
                  <span class="term__field-label">Description</span>
                  <term-rich-editor
                    [value]="editContentFor(l.locale)"
                    [placeholder]="
                      'Edit the T&C copy in ' + l.label + ' — use the toolbar to format'
                    "
                    (valueChange)="onEditContentChange(l.locale, $event)"
                  ></term-rich-editor>
                </div>
              </div>
            </section>
            }
          </div>

          @if (!canSaveEdit()) {
          <p class="term__required-note" role="alert">
            Enter a name in at least one language to save.
          </p>
          }

          <div class="term__form-actions">
            <button
              type="button"
              class="term__btn term__btn--ghost"
              [disabled]="saving()"
              (click)="cancelEdit()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="term__btn term__btn--primary"
              [disabled]="!canSaveEdit() || saving()"
              (click)="saveEdit()"
            >
              <mat-icon aria-hidden="true">save</mat-icon>
              {{ saving() ? "Saving…" : "Save changes" }}
            </button>
          </div>
        </div>
        } } @else {
        <p class="term__hint term__hint--muted">
          Choose an Agreement to preview the customer-facing T&amp;C copy.
        </p>
        } } @else {
        <!-- ── Create form ────────────────────────────────────────── -->
        <header class="term__create-head">
          <h5 class="term__create-title">Create new template</h5>
          <span class="term__hint">
            Author the customer-facing T&amp;C copy per language.
          </span>
        </header>

        <div class="term__field-row">
          <label class="term__field">
            <span class="term__field-label">Type</span>
            <input
              type="text"
              class="term__name-input"
              [value]="draftType()"
              placeholder="package"
              (input)="onDraftTypeInput($event)"
            />
          </label>
          <label class="term__field">
            <span class="term__field-label">Status</span>
            <input
              type="text"
              class="term__name-input"
              [value]="draftStatus()"
              placeholder="initialized"
              (input)="onDraftStatusInput($event)"
            />
          </label>
        </div>

        <div class="term__lang-stack">
          @for (l of visibleLanguages(); track trackLangLocale($index, l)) {
          <section class="term__lang-card">
            <header class="term__lang-card-head">
              <span
                class="term__locale-dot"
                [class.term__locale-dot--on]="
                  (draftNameFor(l.locale) || '').trim().length > 0 ||
                  (draftContentFor(l.locale) || '').trim().length > 0
                "
                aria-hidden="true"
              ></span>
              <span class="term__lang-card-code">{{ l.shortCode }}</span>
              <span class="term__lang-card-label">{{ l.label }}</span>
            </header>

            <div class="term__lang-card-body">
              <label
                class="term__field"
                [class.term__field--invalid]="!draftHasName()"
              >
                <span class="term__field-label"
                  >Name
                  <span class="term__req" aria-hidden="true">*</span></span
                >
                <input
                  type="text"
                  class="term__name-input"
                  [value]="draftNameFor(l.locale)"
                  [placeholder]="'Name in ' + l.label"
                  (input)="onDraftNameInput(l.locale, $event)"
                />
                @if (!draftHasName()) {
                <p class="term__field-hint">
                  Enter a name in at least one language.
                </p>
                }
              </label>

              <div
                class="term__field"
                [class.term__field--invalid]="!draftHasContent()"
              >
                <span class="term__field-label"
                  >Description
                  <span class="term__req" aria-hidden="true">*</span></span
                >
                <term-rich-editor
                  [value]="draftContentFor(l.locale)"
                  [placeholder]="
                    'Author the T&C copy in ' + l.label + ' — use the toolbar to format'
                  "
                  (valueChange)="onDraftContentChange(l.locale, $event)"
                ></term-rich-editor>
                @if (!draftHasContent()) {
                <p class="term__field-hint">
                  Enter a description in at least one language.
                </p>
                }
              </div>
            </div>
          </section>
          }
        </div>

        <div class="term__form-actions">
          <button
            type="button"
            class="term__btn term__btn--ghost"
            (click)="cancelCreate()"
          >
            Cancel
          </button>
          <button
            type="button"
            class="term__btn term__btn--primary"
            [disabled]="!canSaveDraft()"
            (click)="saveDraft()"
          >
            <mat-icon aria-hidden="true">save</mat-icon>
            Save template
          </button>
        </div>
        }
      </div>
    </enr-collapsible>
  </div>
</div>
} @else {
<div
  class="enr-section-card enr-section-card--readonly term-blank"
  role="status"
>
  <div class="term-blank__title">No package loaded</div>
  <div class="term-blank__text">
    The Terms editor will appear once the package finishes loading.
  </div>
</div>
}
